import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import './ScoresScreen.css';
import { fetchUserScores, fetchDailyLeaderboard, fetchWeeklyLeaderboard, fetchCategories } from '../services/apiService';

const ScoresScreen = ({ user, onClose }) => {
  const { t } = useTranslation();
  const [activeTab, setActiveTab] = useState('user-scores');
  const [userScores, setUserScores] = useState([]);
  const [dailyLeaderboardHard, setDailyLeaderboardHard] = useState([]);
  const [dailyLeaderboardMedium, setDailyLeaderboardMedium] = useState([]);
  const [dailyLeaderboardEasy, setDailyLeaderboardEasy] = useState([]);
  const [weeklyLeaderboardHard, setWeeklyLeaderboardHard] = useState([]);
  const [weeklyLeaderboardMedium, setWeeklyLeaderboardMedium] = useState([]);
  const [weeklyLeaderboardEasy, setWeeklyLeaderboardEasy] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [categories, setCategories] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [showCategoriesModal, setShowCategoriesModal] = useState(false);

  // Fetch categories when component mounts
  useEffect(() => {
    const loadCategories = async () => {
      try {
        const categoriesData = await fetchCategories();
        setCategories(categoriesData);
      } catch (err) {
        console.error('Error fetching categories:', err);
      }
    };

    loadCategories();
  }, []);

  // Fetch scores when component mounts or when activeTab, user, or selectedCategory changes
  useEffect(() => {
    const loadScores = async () => {
      setLoading(true);
      setError(null);

      try {
        if (activeTab === 'user-scores' && user) {
          const scores = await fetchUserScores();
          setUserScores(scores);
        } else if (activeTab === 'daily-leaderboard') {
          // Fetch all three difficulty levels
          const hardLeaderboard = await fetchDailyLeaderboard('hard', selectedCategory);
          const mediumLeaderboard = await fetchDailyLeaderboard('medium', selectedCategory);
          const easyLeaderboard = await fetchDailyLeaderboard('easy', selectedCategory);

          setDailyLeaderboardHard(hardLeaderboard);
          setDailyLeaderboardMedium(mediumLeaderboard);
          setDailyLeaderboardEasy(easyLeaderboard);
        } else if (activeTab === 'weekly-leaderboard') {
          // Fetch all three difficulty levels for weekly leaderboard
          const hardLeaderboard = await fetchWeeklyLeaderboard('hard', selectedCategory);
          const mediumLeaderboard = await fetchWeeklyLeaderboard('medium', selectedCategory);
          const easyLeaderboard = await fetchWeeklyLeaderboard('easy', selectedCategory);

          setWeeklyLeaderboardHard(hardLeaderboard);
          setWeeklyLeaderboardMedium(mediumLeaderboard);
          setWeeklyLeaderboardEasy(easyLeaderboard);
        }
      } catch (err) {
        console.error('Error fetching scores:', err);
        setError(t('scores.errorLoading'));
      } finally {
        setLoading(false);
      }
    };

    loadScores();
  }, [activeTab, user, selectedCategory, t]);

  // Handle tab change
  const handleTabChange = (tab) => {
    setActiveTab(tab);
  };

  // Format date
  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString();
  };

  // Format difficulty
  const formatDifficulty = (difficulty) => {
    switch (difficulty) {
      case 'easy':
        return t('difficulty.easy');
      case 'medium':
        return t('difficulty.medium');
      case 'hard':
        return t('difficulty.hard');
      // Handle legacy difficulty values for backward compatibility
      case 'facil':
        return t('difficulty.easy');
      case 'medio':
        return t('difficulty.medium');
      case 'dificil':
        return t('difficulty.hard');
      default:
        return difficulty;
    }
  };

  // Handle category selection
  const handleCategorySelect = (category) => {
    setSelectedCategory(category);
    setShowCategoriesModal(false);
  };

  // Clear category filter
  const clearCategoryFilter = () => {
    setSelectedCategory(null);
  };

  return (
    <div id="scores-screen" className="scores-screen">
      <div className="scores-container">
        <button className="close-button-x" onClick={onClose}>
          <i className="fas fa-times"></i>
        </button>
        <h1>
          <i className="fas fa-trophy"></i> {t('scores.title')}
        </h1>

        <div className="scores-tabs">
          <button 
            className={`tab-button ${activeTab === 'user-scores' ? 'active' : ''}`} 
            onClick={() => handleTabChange('user-scores')}
            disabled={!user}
          >
            <i className="fas fa-user"></i> {t('scores.userScores')}
          </button>
          <button 
            className={`tab-button ${activeTab === 'daily-leaderboard' ? 'active' : ''}`} 
            onClick={() => handleTabChange('daily-leaderboard')}
          >
            <i className="fas fa-calendar-day"></i> {t('scores.dailyLeaderboard')}
          </button>
          <button 
            className={`tab-button ${activeTab === 'weekly-leaderboard' ? 'active' : ''}`} 
            onClick={() => handleTabChange('weekly-leaderboard')}
          >
            <i className="fas fa-calendar-week"></i> {t('scores.weeklyLeaderboard')}
          </button>
          {(activeTab === 'daily-leaderboard' || activeTab === 'weekly-leaderboard') && (
            <button 
              className="tab-button categories-button"
              onClick={() => setShowCategoriesModal(true)}
            >
              <i className="fas fa-filter"></i> {t('scores.categories')}
            </button>
          )}
        </div>

        {/* Categories Modal */}
        {showCategoriesModal && (
          <div className="categories-modal">
            <div className="categories-modal-content">
              <h3>{t('scores.selectCategory')}</h3>
              <button className="close-button" onClick={() => setShowCategoriesModal(false)}>
                <i className="fas fa-times"></i>
              </button>
              <div className="categories-list">
                <button 
                  className={`category-button ${selectedCategory === null ? 'active' : ''}`}
                  onClick={clearCategoryFilter}
                >
                  {t('scores.allCategories')}
                </button>
                {categories.map((category, index) => (
                  <button 
                    key={index} 
                    className={`category-button ${selectedCategory === category ? 'active' : ''}`}
                    onClick={() => handleCategorySelect(category)}
                  >
                    {t(`gamemode.${category.toLowerCase()}.${category.toLowerCase()}`, category.toLowerCase())}
                  </button>
                ))}
              </div>
            </div>
          </div>
        )}

        <div className="tab-content">
          {/* User Scores Tab */}
          <div className={`tab-pane ${activeTab === 'user-scores' ? 'active' : ''}`} id="user-scores-tab">
            <div className="user-scores-container">
              <h2>{t('scores.myScores')}</h2>

              {!user && (
                <div className="login-required">
                  <p>{t('scores.loginRequired')}</p>
                  <a href="/auth/battlenet" className="btn btn-bnet">
                    <span className="bnet-icon">B</span>
                    <span>{t('auth.loginButton')}</span>
                  </a>
                </div>
              )}

              {user && loading && (
                <p className="loading-message">{t('scores.loading')}</p>
              )}

              {user && error && (
                <p className="error-message">{error}</p>
              )}

              {user && !loading && !error && userScores.length === 0 && (
                <p className="no-scores-message">{t('scores.noScores')}</p>
              )}

              {user && !loading && !error && userScores.length > 0 && (
                <div className="scores-list">
                  <table>
                    <thead>
                      <tr>
                        <th>{t('scores.date')}</th>
                        <th>{t('scores.difficulty')}</th>
                        <th>{t('scores.score')}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {userScores.map((score, index) => (
                        <tr key={index}>
                          <td>{formatDate(score.created_at)}</td>
                          <td>{formatDifficulty(score.difficulty)}</td>
                          <td className="score-value">{score.score}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>

          {/* Daily Leaderboard Tab */}
          <div className={`tab-pane ${activeTab === 'daily-leaderboard' ? 'active' : ''}`} id="daily-leaderboard-tab">
            <div className="leaderboard-container">
              <h2>
                {t('scores.todaysLeaders')}
                {selectedCategory && (
                  <span className="selected-category">
                    {t(`gamemode.${selectedCategory.toLowerCase()}.${selectedCategory.toLowerCase()}`, selectedCategory.toLowerCase())}
                    <button className="clear-category" onClick={clearCategoryFilter} title={t('scores.clearCategory')}>
                      <i className="fas fa-times"></i>
                    </button>
                  </span>
                )}
              </h2>

              {loading && (
                <p className="loading-message">{t('scores.loading')}</p>
              )}

              {error && (
                <p className="error-message">{error}</p>
              )}

              {!loading && !error && 
               dailyLeaderboardHard.length === 0 && 
               dailyLeaderboardMedium.length === 0 && 
               dailyLeaderboardEasy.length === 0 && (
                <p className="no-scores-message">{t('scores.noLeaderboard')}</p>
              )}

              {!loading && !error && (
                <>
                  {/* Hard difficulty at the top */}
                  {dailyLeaderboardHard.length > 0 && (
                    <div className="scores-list">
                      <h3>{t('difficulty.hard')}</h3>
                      <table>
                        <thead>
                          <tr>
                            <th>{t('scores.rank')}</th>
                            <th>{t('scores.player')}</th>
                            <th>{t('scores.score')}</th>
                          </tr>
                        </thead>
                        <tbody>
                          {dailyLeaderboardHard.map((entry, index) => (
                            <tr key={index} className={user && entry.user_id === user.id ? 'current-user' : ''}>
                              <td className="rank">{index + 1}</td>
                              <td>{entry.username || entry.battlenet_tag || t('scores.anonymousUser')}</td>
                              <td className="score-value">{entry.score}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}

                  {/* Medium and Easy difficulty levels */}
                  <div className="difficulty-levels-container">
                    {/* Medium difficulty */}
                    <div className="scores-list difficulty-medium">
                      <h3>{t('difficulty.medium')}</h3>
                      {dailyLeaderboardMedium.length === 0 ? (
                        <p className="no-scores-message">{t('scores.noLeaderboard')}</p>
                      ) : (
                        <table>
                          <thead>
                            <tr>
                              <th>{t('scores.rank')}</th>
                              <th>{t('scores.player')}</th>
                              <th>{t('scores.score')}</th>
                            </tr>
                          </thead>
                          <tbody>
                            {dailyLeaderboardMedium.map((entry, index) => (
                              <tr key={index} className={user && entry.user_id === user.id ? 'current-user' : ''}>
                                <td className="rank">{index + 1}</td>
                                <td>{entry.username || entry.battlenet_tag || t('scores.anonymousUser')}</td>
                                <td className="score-value">{entry.score}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      )}
                    </div>

                    {/* Easy difficulty */}
                    <div className="scores-list difficulty-easy">
                      <h3>{t('difficulty.easy')}</h3>
                      {dailyLeaderboardEasy.length === 0 ? (
                        <p className="no-scores-message">{t('scores.noLeaderboard')}</p>
                      ) : (
                        <table>
                          <thead>
                            <tr>
                              <th>{t('scores.rank')}</th>
                              <th>{t('scores.player')}</th>
                              <th>{t('scores.score')}</th>
                            </tr>
                          </thead>
                          <tbody>
                            {dailyLeaderboardEasy.map((entry, index) => (
                              <tr key={index} className={user && entry.user_id === user.id ? 'current-user' : ''}>
                                <td className="rank">{index + 1}</td>
                                <td>{entry.username || entry.battlenet_tag || t('scores.anonymousUser')}</td>
                                <td className="score-value">{entry.score}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      )}
                    </div>
                  </div>
                </>
              )}
            </div>
          </div>

          {/* Weekly Leaderboard Tab */}
          <div className={`tab-pane ${activeTab === 'weekly-leaderboard' ? 'active' : ''}`} id="weekly-leaderboard-tab">
            <div className="leaderboard-container">
              <h2>
                {t('scores.weeklyLeaders')}
                {selectedCategory && (
                  <span className="selected-category">
                    {t(`gamemode.${selectedCategory.toLowerCase()}.${selectedCategory.toLowerCase()}`, selectedCategory.toLowerCase())}
                    <button className="clear-category" onClick={clearCategoryFilter} title={t('scores.clearCategory')}>
                      <i className="fas fa-times"></i>
                    </button>
                  </span>
                )}
              </h2>

              {loading && (
                <p className="loading-message">{t('scores.loading')}</p>
              )}

              {error && (
                <p className="error-message">{error}</p>
              )}

              {!loading && !error && 
               weeklyLeaderboardHard.length === 0 && 
               weeklyLeaderboardMedium.length === 0 && 
               weeklyLeaderboardEasy.length === 0 && (
                <p className="no-scores-message">{t('scores.noLeaderboard')}</p>
              )}

              {!loading && !error && (
                <>
                  {/* Hard difficulty at the top */}
                  {weeklyLeaderboardHard.length > 0 && (
                    <div className="scores-list">
                      <h3>{t('difficulty.hard')}</h3>
                      <table>
                        <thead>
                          <tr>
                            <th>{t('scores.rank')}</th>
                            <th>{t('scores.player')}</th>
                            <th>{t('scores.score')}</th>
                          </tr>
                        </thead>
                        <tbody>
                          {weeklyLeaderboardHard.map((entry, index) => (
                            <tr key={index} className={user && entry.user_id === user.id ? 'current-user' : ''}>
                              <td className="rank">{index + 1}</td>
                              <td>{entry.username || entry.battlenet_tag || t('scores.anonymousUser')}</td>
                              <td className="score-value">{entry.score}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}

                  {/* Medium and Easy difficulty levels */}
                  <div className="difficulty-levels-container">
                    {/* Medium difficulty */}
                    <div className="scores-list difficulty-medium">
                      <h3>{t('difficulty.medium')}</h3>
                      {weeklyLeaderboardMedium.length === 0 ? (
                        <p className="no-scores-message">{t('scores.noLeaderboard')}</p>
                      ) : (
                        <table>
                          <thead>
                            <tr>
                              <th>{t('scores.rank')}</th>
                              <th>{t('scores.player')}</th>
                              <th>{t('scores.score')}</th>
                            </tr>
                          </thead>
                          <tbody>
                            {weeklyLeaderboardMedium.map((entry, index) => (
                              <tr key={index} className={user && entry.user_id === user.id ? 'current-user' : ''}>
                                <td className="rank">{index + 1}</td>
                                <td>{entry.username || entry.battlenet_tag || t('scores.anonymousUser')}</td>
                                <td className="score-value">{entry.score}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      )}
                    </div>

                    {/* Easy difficulty */}
                    <div className="scores-list difficulty-easy">
                      <h3>{t('difficulty.easy')}</h3>
                      {weeklyLeaderboardEasy.length === 0 ? (
                        <p className="no-scores-message">{t('scores.noLeaderboard')}</p>
                      ) : (
                        <table>
                          <thead>
                            <tr>
                              <th>{t('scores.rank')}</th>
                              <th>{t('scores.player')}</th>
                              <th>{t('scores.score')}</th>
                            </tr>
                          </thead>
                          <tbody>
                            {weeklyLeaderboardEasy.map((entry, index) => (
                              <tr key={index} className={user && entry.user_id === user.id ? 'current-user' : ''}>
                                <td className="rank">{index + 1}</td>
                                <td>{entry.username || entry.battlenet_tag || t('scores.anonymousUser')}</td>
                                <td className="score-value">{entry.score}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      )}
                    </div>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>

        <button className="btn btn-secondary close-btn" onClick={onClose}>
          <i className="fas fa-times"></i> {t('common.close')}
        </button>
      </div>
    </div>
  );
};

export default ScoresScreen;
